import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import PermissionGroupService from "../../services/doctorServices/PermissionGroupService";
import Headeer from "../../components/Headeer";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LockIcon from "@mui/icons-material/Lock";
import ConfirmationDialog from "../../components/ConfirmationDialog";

export default function PermissionGroupsList() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('permissionGroups');
  const navigate = useNavigate();
  const permissionService = new PermissionGroupService();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAllGroups();
      // Filter only doctor type groups for doctor admin
      let doctorGroups = data.filter(group => group.type === 'doctor');
      
      // Get current doctor role
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentRole = user.role;
      
      // If current user is 'doctor' role (not admin), filter out FULL_DOCTOR default group
      if (currentRole === 'doctor') {
        doctorGroups = doctorGroups.filter(group => 
          !(group.name === 'FULL_DOCTOR' && group.is_default === true)
        );
      }
      
      setGroups(doctorGroups);
    } catch (error) {
      console.error("Error fetching permission groups:", error);
      setSnackbar({
        open: true,
        message: t('errorLoadingGroups'),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (group) => {
    if (group.is_default) {
      setSnackbar({
        open: true,
        message: t('defaultGroupsCannotDelete'),
        severity: "warning",
      });
      return;
    }
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await permissionService.deleteGroup(selectedGroup.id);
      setSnackbar({
        open: true,
        message: t('groupDeletedSuccessfully'),
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 
        error.response?.data?.error || 
        t('errorDeletingGroup');
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const countPermissions = (group) => {
    const permissions = Object.keys(group).filter(key => 
      key.startsWith('can_') && group[key] === true
    );
    return permissions.length;
  };

  if (loading) {
    return (
      <Box m="20px">
        <Headeer title={t('title')} subtitle={t('loadingPermissionGroups')} />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Typography variant="h5" sx={{ color: colors.grey[300] }}>
            {t('loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Headeer title={t('title')} subtitle={t('subtitle')} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/doctor-permission-groups/create")}
          sx={{
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            color: "white",
            px: 3,
            py: 1.5,
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
            },
            transition: "all 0.3s ease",
          }}
        >
          {t('createNewGroup')}
        </Button>
      </Box>

      {groups.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <SecurityIcon sx={{ fontSize: 64, color: colors.grey[500], mb: 2 }} />
          <Typography variant="h5" sx={{ color: colors.grey[100], mb: 1 }}>
            {t('noGroupsYet')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3 }}>
            {t('createFirstGroupMessage')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/doctor-permission-groups/create")}
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {t('createFirstGroup')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
                  border: `1px solid ${colors.grey[800]}30`,
                  borderRadius: "16px",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MedicalServicesIcon sx={{ color: "white", fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                          {group.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                          {group.name}
                        </Typography>
                      </Box>
                    </Box>

                    {group.is_default && (
                      <Tooltip title={t('defaultGroupTooltip')}>
                        <Chip
                          icon={<LockIcon sx={{ fontSize: 14 }} />}
                          label={t('default')}
                          size="small"
                          sx={{
                            backgroundColor: `${colors.blueAccent[800]}40`,
                            color: colors.blueAccent[300],
                            fontWeight: 600,
                            border: `1px solid ${colors.blueAccent[500]}40`,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.grey[300],
                      mb: 2,
                      minHeight: "40px",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {group.description || t('noDescription')}
                  </Typography>

                  {/* Stats */}
                  <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: "120px",
                        p: 1.5,
                        borderRadius: "10px",
                        background: `${colors.greenAccent[800]}20`,
                        border: `1px solid ${colors.greenAccent[500]}30`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                        {t('type')}
                      </Typography>
                      <Chip
                        label={t('doctor')}
                        size="small"
                        sx={{
                          backgroundColor: colors.greenAccent[700],
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: "120px",
                        p: 1.5,
                        borderRadius: "10px",
                        background: `${colors.greenAccent[800]}20`,
                        border: `1px solid ${colors.greenAccent[500]}30`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                        {t('permissions')}
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.greenAccent[300], fontWeight: 700 }}>
                        {countPermissions(group)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: "120px",
                        p: 1.5,
                        borderRadius: "10px",
                        background: `${colors.blueAccent[800]}20`,
                        border: `1px solid ${colors.blueAccent[500]}30`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                        {t('createdBy')}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: colors.grey[100], 
                          fontWeight: 600,
                          fontSize: "0.85rem",
                        }}
                      >
                        {group.created_by ? `${t('doctorPrefix')}${group.created_by.substring(0, 8)}...` : t('system')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title={t('viewDetails')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/doctor-permission-groups/${group.id}`)}
                        sx={{
                          flex: 1,
                          borderRadius: "8px",
                          backgroundColor: `${colors.blueAccent[700]}40`,
                          border: `1px solid ${colors.blueAccent[500]}40`,
                          "&:hover": {
                            backgroundColor: `${colors.blueAccent[600]}60`,
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
                      </IconButton>
                    </Tooltip>

                    {!group.is_default && (
                      <>
                        <Tooltip title={t('editGroup')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/doctor-permission-groups/edit/${group.id}`)}
                            sx={{
                              flex: 1,
                              borderRadius: "8px",
                              backgroundColor: `${colors.greenAccent[700]}40`,
                              border: `1px solid ${colors.greenAccent[500]}40`,
                              "&:hover": {
                                backgroundColor: `${colors.greenAccent[600]}60`,
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18, color: colors.greenAccent[300] }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('deleteGroup')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(group)}
                            sx={{
                              flex: 1,
                              borderRadius: "8px",
                              backgroundColor: `${colors.redAccent[700]}40`,
                              border: `1px solid ${colors.redAccent[500]}40`,
                              "&:hover": {
                                backgroundColor: `${colors.redAccent[600]}60`,
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18, color: colors.redAccent[300] }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t('deletePermissionGroup')}
        description={
          selectedGroup
            ? t('deleteConfirmMessage', { label: selectedGroup.label })
            : ""
        }
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}







